import { z } from 'zod';

//#region Shared Types

export enum Directions {
    InBound = 'IN',
    OutBound = 'OUT',
}

const DirectionSchema = z.nativeEnum(Directions);

export type Direction = z.infer<typeof DirectionSchema>;

export enum GeoTypes {
    Country = 'COUNTRY',
    Region = 'REGION',
    State = 'STATE',
    ExtendedMarket = 'X_MKT',
    Market = 'MKT',
    ThreeDZ = 'THREEDZ',
}

export const GeoTypesSchema = z.nativeEnum(GeoTypes);

export type GeoType = z.infer<typeof GeoTypesSchema>;

export enum TimeFrames {
    ThirtyDaysBack = 'THIRTY_DAYS_BACK',
    EightDaysBack = 'EIGHT_DAYS_BACK',
    PrevBusinessDay = 'PREV_BUSINESS_DAY',
    Current = 'CURRENT',
    EightDayForecast = 'EIGHT_DAY_FORECAST',
}

export const TimeFrameSchema = z.nativeEnum(TimeFrames);

export type TimeFrame = z.infer<typeof TimeFrameSchema>;

export enum EquipmentTypes {
    Van = 'VAN',
    Reefer = 'REEFER',
    FlatBed = 'FLATBED',
}

export const EquipmentTypeSchema = z.union([z.nativeEnum(EquipmentTypes), z.literal('')]);

export type EquipmentType = z.infer<typeof EquipmentTypeSchema>;

export const ControlValueSchema = z.object({
    display: z.string(),
    value: z.string(),
});

export type ControlValue = z.infer<typeof ControlValueSchema>;

export const ControlSchema = z.object({
    controlName: z.string(),
    value: ControlValueSchema.or(z.string()),
});

export type Control = z.infer<typeof ControlSchema>;

export const RouteParamsSchema = z.object({
    equipmentType: z.enum(['VAN', 'REEFER', 'FLATBED']).default('VAN'),
    direction: z.enum(['IN', 'OUT']).default('OUT'),
    timeframe: z.enum(['30_DAYS_BACK', '8_DAYS_BACK', 'PREV_BUSINESS_DAY', 'CURRENT', 'FORECAST']).default('CURRENT'),
    geoType: z.enum(['COUNTRY', 'REGION', 'STATE', 'XMA', 'KMA', 'THREEDZ']).default('KMA'),
    geoCode: z.string().optional().default(''),
    displayName: z.string().optional().default(''),
    daysForward: z.number().optional().default(0),
});

export type RouteParams = z.infer<typeof RouteParamsSchema>;

export const EquipmentTypeControls : Record<string,ControlValue> = {
    'VAN': { display: 'Van', value: 'VAN' },
    'REEFER': { display: 'Reefer', value: 'REEFER' },
    'FLATBED': { display: 'Flatbed', value: 'FLATBED' },
}

export const DirectionControls : Record<string,ControlValue> = {
    'IN': { display: 'Inbound', value: 'IN' },
    'OUT': { display: 'Outbound', value: 'OUT' },
}

export const TimeframeControls : Record<string,ControlValue> = {
    '30_DAYS_BACK': { display: 'L30D', value: 'THIRTY_DAYS_BACK' },
    '8_DAYS_BACK': { display: 'L8D', value: 'EIGHT_DAYS_BACK' },
    'PREV_BUSINESS_DAY': { display: 'LD', value: 'PREV_BUSINESS_DAY' },
    'CURRENT': { display: 'Current', value: 'CURRENT' },
    'FORECAST': { display: '+8D', value: 'EIGHT_DAY_FORECAST' },
}

export const GeoTypeControls : Record<string,ControlValue> = {
    'COUNTRY': { display: 'Country', value: 'COUNTRY' },
    'REGION': { display: 'Region', value: 'REGION' },
    'STATE': { display: 'State', value: 'STATE' },
    'XMA': { display: 'EXT MKT', value: 'X_MKT' },
    'KMA': { display: 'MKT', value: 'MKT' },
    'THREEDZ': { display: '3DZ', value: 'THREEDZ' },
};


// These route maps are required to adhere to the old routing conventions
export const TimeframeRouteMap : Record<string, string> = {
    'THIRTY_DAYS_BACK': '30_DAYS_BACK',
    'EIGHT_DAYS_BACK': '8_DAYS_BACK',
    'PREV_BUSINESS_DAY': 'PREV_BUSINESS_DAY',
    'CURRENT': 'CURRENT',
    'EIGHT_DAY_FORECAST' : 'FORECAST',
}

export const GeoTypeRouteMap: Record<string, string> = {
    'COUNTRY': 'COUNTRY',
    'REGION': 'REGION',
    'STATE': 'STATE',
    'X_MKT': 'XMA',
    'MKT': 'KMA',
    'THREEDZ': 'THREEDZ',
}

export const McResponseSchema = z.object({
    fetchInMs: z.number().optional().default(-1),
    loadedInMs: z.number().optional(),
    durationInMs: z.number().optional(),
    fetch: z
        .boolean()
        .describe('Whether the data is currently being fetched')
        .default(false),
    loaded: z
        .boolean()
        .describe('Whether the data has been loaded')
        .default(false),
    hasError: z
        .boolean()
        .describe('Whether the data has an error')
        .default(false),
    error: z.string().optional().default(''),
});

export type McResponse = z.infer<typeof McResponseSchema>;

/**
 * These are the keys in the lane schema that should trigger a fetch.
 * The exact logic for these fetches will be handled by the various services.
 *
 * For each of these we will need an action in the data service to consume the lane and the action
 * and a event that will call once the data is retrieved.
 *
 */
export const fetchKeys: string[] = ['Mci', 'Lt', 'Area'];

export const createConditionKey = (
    equipmentType: EquipmentType,
    geoType: GeoTypes,
    direction: Direction,
) => {
    return [equipmentType, geoType, direction].join('.');
};

export const createLtKey = (
    equipmentType: EquipmentType,
    geoType: GeoTypes,
    timeframe: TimeFrame,
) => {
    return [equipmentType, geoType, timeframe].join('.');
};
export const createMciKey = (
    equipmentType: EquipmentType,
    geoType: GeoTypes,
    direction: Direction,
    timeframe: TimeFrame,
) => {
    return [equipmentType, geoType, direction, timeframe].join('.');
};

export const createMciForecastKey = (
    equipmentType: EquipmentType,
    geoType: GeoTypes,
    direction: Direction,
    timeframe: TimeFrame,
    forecastDate: string,
) => {
    return [equipmentType, geoType, direction, timeframe, forecastDate].join('.');
};

export const createGeoKey = (geoCode: string) => {
    return [geoCode].join('.');
};

/**
 * Creates the event names for the condition for to be fetched.
 *
 * @param fetchKey - the key for the fetch
 * @param suffix - the suffix for the fetch
 */
export const createMcEventNames = (
    fetchKey: string,
    suffix: string[] = [],
): string => {
    return `type:mc-fetch:${fetchKey}${
        !!suffix?.length ? ':' + suffix?.join(':') : ''
    }`;
};

//#endregion

//#region Location
export const McLocationQueryTypeSchema = z.union([
    z.literal('location'),
    z.literal('area'),
]);
export type McLaneQueryType = z.infer<typeof McLocationQueryTypeSchema>;

/**
 * This is a query to get the locations for a given display name
 */
export const McLocationQuerySchema = z
    .object({
        id: z.string(),
        type: McLocationQueryTypeSchema,
        displayName: z.string().optional().default(''),
        area: GeoTypesSchema.default(GeoTypes.Market),
        geoCode: z.string().default(''),
    })
    .strict();

/**
 * This is the response for the location query
 * This location will have the display name and the various information
 * from the GeoService pulled from the Facade. Also, this will list all the
 * areas this Geo is in.
 */
export const McLocationSchema = McResponseSchema.merge(
    z.object({
        id: z.string().default(''),
        city: z.string().default(''),
        county: z.string().default(''),
        displayName: z.string().default(''),
        latitude: z.number().default(0),
        longitude: z.number().default(0),
        placeId: z.number().default(0),
        postalCode: z.string().default(''),
        stateProv: z.string().default(''),
        /**
         * This is a map of the geo codes this location relates to
         *
         */
        geoCodes: z
            .record(
                GeoTypesSchema,
                z.array(
                    z.object({
                        code: z.string(),
                        type: GeoTypesSchema,
                        name: z.string(),
                    }),
                ),
            )
            .default({}),
    }),
);

/**
 * The query that is used to get the rate
 *
 * @property `id` - The id of the query
 * @property `type` - The type of the query
 * @property `displayName` - The display name of the location
 */
export type McLocationQuery = z.infer<typeof McLocationQuerySchema>;
export type McLocationGeoCode = z.infer<typeof McLocationSchema>['geoCodes'];
export type McLocation = z.infer<typeof McLocationSchema>;
//#endregion

//#region MC Query

export const McQuerySchema = z.object({
    id: z.string(),
    type: McLocationQueryTypeSchema,
    direction: DirectionSchema,
    geoType: GeoTypesSchema,
    geoCode: z.string().default(''),
    equipmentType: EquipmentTypeSchema,
    timeframe: TimeFrameSchema.default(TimeFrames.Current),
    displayName: z.string().or(z.null()).default(''),
    daysForward: z.string().or(z.null()).default(''),
    forecastDate: z.string().or(z.null()).default(''),
});

export const CapacityLevelsSchema = z.union([
    z.literal('Very Loose'),
    z.literal('Moderately Loose'),
    z.literal('Slightly Loose'),
    z.literal('Neutral Market'),
    z.literal('Slightly Tight'),
    z.literal('Moderately Tight'),
    z.literal('Very Tight'),
]);
export type CapacityLevels = z.infer<typeof CapacityLevelsSchema>;

export type McQuery = z.infer<typeof McQuerySchema>;

//#endregion

//#region MCI

const McResponseMciTimelineSchema = McResponseSchema.merge(
    z.object({
        date: z.string().default(''),
        code: z.string().default(''),
        name: z.string().default(''),
        mci: z.number().or(z.null()).default(null),
        predictedIntervalUpperBound: z.number().or(z.null()).default(null),
        predictedIntervalLowerBound: z.number().or(z.null()).default(null),
        capacity: CapacityLevelsSchema,
        calculationDate: z.string().default(''),
        type: GeoTypesSchema,
        equipmentType: EquipmentTypeSchema,
    }),
);

export type McMciTimeline = z.infer<typeof McResponseMciTimelineSchema>;

export const McResponseMciSchema = McResponseSchema.merge(
    z.object({
        date: z.string().default(''),
        code: z.string().default(''),
        name: z.string().default(''),
        mci: z.number().default(0),
        predictedIntervalUpperBound: z.number().default(0),
        predictedIntervalLowerBound: z.number().default(0).nullable(),
        capacity: CapacityLevelsSchema.default('Neutral Market'),
        capacityColor: z.string().default(''),
        direction: DirectionSchema.default(Directions.InBound),
        timeframe: TimeFrameSchema.default(TimeFrames.Current),
        capacityMap: z
            .record(
                CapacityLevelsSchema,
                z.object({
                    high: z.number().default(0),
                    low: z.number().default(0),
                    color: z.string().default(''),
                    index: z.number().default(0),
                }),
            )
            .default({
                'Very Loose': { high: 0, low: 0, color: '', index: 0 },
                'Moderately Loose': { high: 0, low: 0, color: '', index: 1 },
                'Slightly Loose': { high: 0, low: 0, color: '', index: 2 },
                'Neutral Market': { high: 0, low: 0, color: '', index: 3 },
                'Slightly Tight': { high: 0, low: 0, color: '', index: 4 },
                'Moderately Tight': { high: 0, low: 0, color: '', index: 5 },
                'Very Tight': { high: 0, low: 0, color: '', index: 6 },
            }),
        calculationDate: z.string().default(''),
        type: GeoTypesSchema.default(GeoTypes.Market),
        equipmentType: EquipmentTypeSchema.default(''),
        /**
         * This is for Forecast, Prior 8 Days, Prior 30 Days
         * so we can add the data there. The reason we aren't
         * putting it at the root is for convience.
         *
         * We will make this a record instead of an array since it will
         * make fetching data in here much easier and we would need to sort
         * these are the UI level anyways.
         *
         * This key string will be date for each of the values
         * for this data with underscores.
         *
         * > Note: Underscores are so we can have easier time with the keys since
         *
         * ```
         * {
         *   '08_01_2024': { ... },
         *   '08_02_2024': { ... },
         *   '08_03_2024': { ... },
         * }
         * ```
         *
         * The reason for a map instead of an array/object
         * is Maps are more performant and we can get the data
         * easier once mapped
         *
         * So if I wanted to get August 1st, 2024 for the Denver MKT the path would be
         *```
         * get(score, ['MCI.DEN_CO.8_DAY_FORECAST', 'timeline.2024_08_01', 'mci'])
         * ```
         */
        timeline:z.record(z.string(), McResponseMciTimelineSchema).default({}),
    }),
);

export type McResponseMci = z.infer<typeof McResponseMciSchema>;

//#endregion

//#region Lt

const McLtTimelineResponse = McResponseSchema.merge(
    z.object({
        date: z.string().default(''),
        code: z.string().default(''),
        name: z.string().default(''),
        loads: z.number().default(0).nullable(),
        trucks: z.number().default(0).nullable(),
        ratio: z.number().default(0).nullable(),
        type: GeoTypesSchema.default(GeoTypes.Market),
        equipmentType: EquipmentTypeSchema.default(''),
    }),
);

export type McLtTimeline = z.infer<typeof McLtTimelineResponse>;

export const McResponseLtSchema = McResponseSchema.merge(
    z.object({
        date: z.string().default(''),
        code: z.string().default(''),
        name: z.string().default(''),
        timeframe: TimeFrameSchema.default(TimeFrames.Current),
        loads: z.number().default(0),
        trucks: z.number().default(0),
        ratio: z.number().default(0).or(z.null()),
        type: GeoTypesSchema.default(GeoTypes.Market),
        direction: DirectionSchema.default(Directions.InBound),
        equipmentType: EquipmentTypeSchema.default(''),
        /**
         * This is for Forecast, Prior 8 Days, Prior 30 Days
         * so we can add the data there. The reason we aren't
         * putting it at the root is for convience.
         *
         * We will make this a record instead of an array since it will
         * make fetching data in here much easier and we would need to sort
         * these are the UI level anyways.
         *
         * This key string will be date for each of the values
         * for this data with underscores.
         *
         * > Note: Underscores are so we can have easier time with the keys since
         *
         * ```
         * {
         *   '08_01_2024': { ... },
         *   '08_02_2024': { ... },
         *   '08_03_2024': { ... },
         * }
         * ```
         *
         * The reason for a map instead of an array/object
         * is Maps are more performant and we can get the data
         * easier once mapped
         *
         * So if I wanted to get August 1st, 2024 for the Denver MKT the path would be
         *```
         * get(score, ['Lt.DEN_CO.8_DAY_FORECAST', 'timeline.2024_08_01', 'mci'])
         * ```
         */
        timeline: z.record(z.string(), McLtTimelineResponse).default({}),
    }),
);

export type McResponseLt = z.infer<typeof McResponseLtSchema>;

//#endregion
//#region Area

//#endregion

//#region Data Models
export const McConditionSchema = z.object({
    /**
     * The id of this MKT Condition is a composite key of the equipment type, the geography area, and the direction.
     *
     * For example, the id for inbound market area van conditions would be:
     *
     * VAN.MKT.IN
     *
     */
    id: z.string().default(''),
    /**
     * This will map for every area underneath a given area type.
     *
     * For example, if we are getting all the market areas, the example keys for this map would be like
     * ```
     * {
     *    'NM_ALB.30_DAYS_BACK': {...},
     *    'NM_ALB.7_DAYS_BACK': {...},
     *    'NM_ALB.8_DAYS_BACK': {...},
     *    'NM_ALB.PREV_BUSINESS_DAY': {...},
     *    'NM_ALB.Current': {...},
     *    'NM_ALB.8_DAY_FORECAST': {...},
     *     // Rest of the areas
     *
     * }
     * ```
     */
    Mci: z.record(z.string(), McResponseMciSchema).default({}),
    /**
     * This object acts as metadata to keep a check on the fetch status of the entire Area Response
     */
    MciAreaResponseInfo: McResponseSchema.default(McResponseSchema.parse({})),

    /**
     * This will map for every area underneath a given area type.
     *
     * For example, if we are getting all the market areas, the example keys for this map would be like
     * ```
     * {
     *    'NM_ALB.30_DAYS_BACK': {...},
     *    'NM_ALB.7_DAYS_BACK': {...},
     *    'NM_ALB.8_DAYS_BACK': {...},
     *    'NM_ALB.PREV_BUSINESS_DAY': {...},
     *    'NM_ALB.Current': {...},
     *     // Rest of the areas
     *
     * }
     * ```
     */
    Lt: z.record(z.string(), McResponseLtSchema).default({}),

    /**
     *  This object acts as metadata to keep a check on the fetch status of the entire Area Response
     */
    LtAreaResponseInfo: McResponseSchema.default(McResponseSchema.parse({})),
});
export type McCondition = z.infer<typeof McConditionSchema>;
//#endregion